import type { DeliveryRegistrationFormUi } from '@/components/Order/OrderDetails/DeliveryRegistrationForm/DeliveryRegistrationFormUi';
import { initialState } from '@/components/Order/OrderDetails/DeliveryRegistrationForm/DeliveryRegistrationFormUi';
import { useGlobalContext } from '@/context/Global/GlobalContext';
import { useGetDeliveryInfoData } from '@/hooks/order/details/useGetDeliveryInfo';
import { OrderDeliveryInfoResponseToDeliveryRegistrationFormUi } from '@/hooks/order/details/useGetDeliveryInfo/mapper';
import { useUpdateOrderDeliveryInfo } from '@/hooks/order/details/useUpdateDeliveryInfo';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

export function useDeliveryRegistrationFormViewController(
  orderIdProp?: string
) {
  const { orderDetailId } = useParams();
  const orderId = orderIdProp ?? orderDetailId ?? '';
  const { data } = useGetDeliveryInfoData(orderId);
  const { session } = useGlobalContext();
  const updateHook = useUpdateOrderDeliveryInfo();

  const [uiState, setUiState] =
    useImmer<DeliveryRegistrationFormUi>(initialState);

  useEffect(() => {
    if (!data || !session?.type) return;
    setUiState(() =>
      OrderDeliveryInfoResponseToDeliveryRegistrationFormUi(data)
    );
  }, [data, session, setUiState]);

  const onDateChange = (date: dayjs.Dayjs | null) =>
    setUiState((draft) => {
      draft.deliveryConfirmedDate = date;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const onStartTimeChange = (time: dayjs.Dayjs | null) =>
    setUiState((draft) => {
      draft.deliveryConfirmedTime = time;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const onNotesChange = (newValue: string) =>
    setUiState((draft) => {
      draft.deliveryConfirmedNotes = newValue;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const validInput = /^$|^\d+(\.\d{0,2})?$/;

  const onAmountChange = (newValue: string) => {
    if (!validInput.test(newValue)) return;

    setUiState((draft) => {
      draft.deliveryTotalAmount = newValue;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });
  };

  const onOrderCompletedToggle = (checked: boolean) =>
    setUiState((draft) => {
      draft.deliveryOrderCompleted = checked;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const onOrderExtraProductsToggle = (checked: boolean) =>
    setUiState((draft) => {
      draft.deliveryOrderExtraProducts = checked;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const onProductPriceAdjustedToggle = (checked: boolean) =>
    setUiState((draft) => {
      draft.deliveryProductPriceAdjusted = checked;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const onWarrantyToggle = (checked: boolean) =>
    setUiState((draft) => {
      draft.appliedMercantoWarranty = checked;
      draft.saveDisabled = false;
      draft.cancelDisabled = false;
    });

  const onCancelChanges = () => {
    if (!data) return;
    setUiState(() =>
      OrderDeliveryInfoResponseToDeliveryRegistrationFormUi(data)
    );
  };

  const onSaveChanges = () =>
    updateHook.updateOrderDeliveryInfo({ orderId, payload: uiState });

  return {
    uiState,
    actions: {
      onDateChange,
      onStartTimeChange,
      onNotesChange,
      onAmountChange,
      onOrderCompletedToggle,
      onOrderExtraProductsToggle,
      onProductPriceAdjustedToggle,
      onWarrantyToggle,
      onCancelChanges,
      onSaveChanges,
    },
  };
}
